import type { LedgerTicketTier } from '../../lib/closing';

/**
 * Sold against capacity, one grouped bar pair per ticket tier.
 *
 * INLINE SVG RATHER THAN CHART.JS, and it is a deliberate substitution of mechanism, not of
 * appearance. Every value in the spec is reproduced exactly: the fills and borders, the 1px
 * border, the 4px radius, barPercentage 0.6 and categoryPercentage 0.8, no legend, labels
 * truncated and rotated 90 degrees, a y-axis from zero with white ticks and
 * rgba(255,255,255,0.06) gridlines. Chart.js plus its adapter is roughly 200KB for two bar
 * series, on a bundle that already carries a size warning; this is a few hundred bytes and
 * renders the same picture.
 *
 * THE GEOMETRY, which is the part that is easy to get subtly wrong, and which I did get
 * wrong first time. categoryPercentage 0.8 is how much of a tier's slot the bar GROUP
 * occupies. barPercentage 0.6 is then how much of EACH BAR'S OWN SHARE OF THE GROUP that
 * bar fills, not how much of the whole group. With two datasets each gets half the group,
 * so a bar is groupWidth / 2 x 0.6 wide.
 *
 * Reading it as 0.6 of the whole group makes two bars total 1.2 x the group and they
 * overlap, which renders as one bar with a strip of the other showing past its edge.
 *
 * CAPACITY IS DRAWN BEHIND, NOT STACKED. Sold and capacity are not two parts of a whole;
 * capacity is the ceiling sold is measured against. Stacking them would draw a total of
 * sold + capacity, which is a number that does not exist.
 */

const SOLD = '#3ddc97';
const CAP_FILL = 'rgba(255,255,255,0.10)';
const CAP_BORDER = 'rgba(255,255,255,0.18)';
const GRID = 'rgba(255,255,255,0.06)';

const CATEGORY_PERCENTAGE = 0.8;
const BAR_PERCENTAGE = 0.6;
const BAR_RADIUS = 4;

const PLOT_H = 200;
const PAD_LEFT = 44;
const PAD_RIGHT = 8;
const PAD_TOP = 12;
/** Room for a 90 degree rotated label beneath the axis. */
const PAD_BOTTOM = 92;

/** Truncate to the spec's limits, with an ellipsis so a cut label reads as cut. */
function truncate(label: string, max: number): string {
  return label.length > max ? `${label.slice(0, max)}...` : label;
}

/**
 * A "nice" upper bound and four gridlines.
 *
 * Rounded up to a round number rather than set to the maximum value, so the top gridline is
 * a readable figure and the tallest bar does not touch the ceiling, which reads as clipped.
 */
function axisMax(values: number[]): number {
  const raw = Math.max(...values, 1);
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  return Math.ceil(raw / magnitude) * magnitude;
}

/** A bar rounded on its top two corners only, anchored to the baseline. */
function barPath(x: number, y: number, w: number, h: number): string {
  if (h <= 0) return '';
  const r = Math.min(BAR_RADIUS, w / 2, h);
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} `
    + `L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
}

export function TierSalesChart({ tiers, compact = false }: {
  tiers: LedgerTicketTier[];
  /** Mobile: labels truncate at 10 rather than 15. */
  compact?: boolean;
}) {
  if (tiers.length === 0) return null;

  const width = 640;
  const plotW = width - PAD_LEFT - PAD_RIGHT;
  const slot = plotW / tiers.length;
  const groupW = slot * CATEGORY_PERCENTAGE;
  // Two datasets share the group, so each owns half of it and fills BAR_PERCENTAGE of that.
  const DATASETS = 2;
  const bandW = groupW / DATASETS;
  const barW = bandW * BAR_PERCENTAGE;
  const max = axisMax(tiers.flatMap((t) => [t.sold, t.capacity]));
  const yOf = (v: number) => PAD_TOP + PLOT_H - (v / max) * PLOT_H;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <svg
      className="tier-chart"
      viewBox={`0 0 ${width} ${PAD_TOP + PLOT_H + PAD_BOTTOM}`}
      role="img"
      aria-label={`Tickets sold against capacity for ${tiers.length} tier${tiers.length === 1 ? '' : 's'}`}
    >
      {/* Gridlines and y ticks. Recessive by design: they are a reference, not data. */}
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD_LEFT} x2={width - PAD_RIGHT} y1={yOf(t)} y2={yOf(t)}
            stroke={GRID} strokeWidth="1" />
          <text x={PAD_LEFT - 8} y={yOf(t) + 4} textAnchor="end"
            fill="#ffffff" fontSize="11" opacity="0.75">{t.toLocaleString('en-US')}</text>
        </g>
      ))}

      {tiers.map((t, i) => {
        const slotX = PAD_LEFT + i * slot;
        const groupX = slotX + (slot - groupW) / 2;
        // Capacity first, so a sold bar of equal height sits cleanly on top of its own
        // outline rather than being overdrawn by it.
        // Each bar centred inside its own half of the group, so the pair is symmetric about
        // the tier's midpoint and the gap between them is even.
        const capX = groupX + (bandW - barW) / 2;
        const soldX = groupX + bandW + (bandW - barW) / 2;
        const labelX = slotX + slot / 2;
        return (
          <g key={t.name}>
            <path d={barPath(capX, yOf(t.capacity), barW, PLOT_H + PAD_TOP - yOf(t.capacity))}
              fill={CAP_FILL} stroke={CAP_BORDER} strokeWidth="1" />
            <path d={barPath(soldX, yOf(t.sold), barW, PLOT_H + PAD_TOP - yOf(t.sold))}
              fill={SOLD} stroke={SOLD} strokeWidth="1" />
            {/* Rotated about its own anchor so it hangs beneath the axis. */}
            <text x={labelX} y={PAD_TOP + PLOT_H + 10}
              fill="#ffffff" fontSize="11" opacity="0.8"
              textAnchor="end" dominantBaseline="middle"
              transform={`rotate(-90 ${labelX} ${PAD_TOP + PLOT_H + 10})`}>
              {truncate(t.name, compact ? 10 : 15)}
            </text>
          </g>
        );
      })}

      {/* The baseline. Drawn last so bars never cover it. */}
      <line x1={PAD_LEFT} x2={width - PAD_RIGHT} y1={PAD_TOP + PLOT_H} y2={PAD_TOP + PLOT_H}
        stroke={CAP_BORDER} strokeWidth="1" />
    </svg>
  );
}
